import React, { useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { setEditingHolder, setEditingRequisite } from "../store/slices/uiSlice";
import { RootState } from "../store";
import {
  useGetTraderHoldersQuery,
  useToggleHolderMutation,
  useDeleteHolderMutation,
  useToggleNewRequisiteMutation,
  useDeleteNewRequisiteMutation,
} from "../store/trader-api";
import DeviceStatusButton from "./DeviceStatusButton";
import SimpleDeviceButton from "./SimpleDeviceButton";
import QRBindingModal from "./QRBindingModal";

// Иконки
const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

const TableContainer = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-sm);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 650px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TableHeader = styled.thead`
  background: var(--bg-primary);
  border-bottom: 1px solid var(--border-color);

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ====== Mobile Holder Cards ====== */
const MobileHoldersList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
  }
`;

const MobileHolderCard = styled.div<{ expanded?: boolean }>`
  background: var(--bg-card);
  border: 1px solid ${(props) => props.expanded ? 'var(--accent-primary-border)' : 'var(--border-color)'};
  border-radius: 0.75rem;
  overflow: hidden;
  transition: border-color 0.2s;
`;

const MobileHolderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  cursor: pointer;

  &:active {
    background: var(--bg-primary);
  }
`;

const MobileHolderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const MobileHolderName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9375rem;
  margin-bottom: 0.125rem;
`;

const MobileHolderMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.5rem;
`;

const MobileHolderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem 0.75rem;
  border-top: 1px solid var(--border-divider);
  flex-wrap: wrap;
`;

const MobileHolderStats = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0 1rem 0.75rem;
  font-size: 0.8125rem;
`;

const MobileStatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const MobileStatLabel = styled.span`
  font-size: 0.6875rem;
  color: var(--text-muted);
  text-transform: uppercase;
  font-weight: 500;
`;

const MobileStatValue = styled.span`
  font-size: 0.8125rem;
  color: var(--text-primary);
  font-weight: 600;
`;

const MobileRequisitesList = styled.div`
  border-top: 1px solid var(--border-divider);
  background: var(--bg-primary);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileRequisiteCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
`;

const MobileReqHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MobileReqMethod = styled.span`
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--text-primary);
`;

const MobileReqDetails = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const MobileReqActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border-divider);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ clickable?: boolean; expanded?: boolean }>`
  border-bottom: 1px solid var(--border-color);
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.clickable ? "var(--bg-primary)" : "transparent")};
  }

  &:last-child {
    border-bottom: none;
  }

  ${(props) =>
    props.expanded &&
    `
    background: var(--bg-primary);
    border-bottom-color: var(--border-color);
  `}
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const TableCell = styled.td`
  padding: 1rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  vertical-align: top;
`;

const ExpandButton = styled.button<{ expanded: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: var(--text-secondary);
  transition: all 0.2s;
  transform: ${(props) => (props.expanded ? "rotate(90deg)" : "rotate(0deg)")};

  &:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
  }
`;

const HolderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HolderName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
`;

const HolderDetails = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const StatusBadge = styled.span<{ active: boolean }>`
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${(props) => (props.active ? "var(--status-success-bg)" : "var(--status-error-bg)")};
  color: ${(props) => (props.active ? "var(--status-success-text)" : "var(--status-error-text)")};
`;

const ActionButton = styled.button<{
  variant?: "danger" | "secondary" | "edit";
}>`
  padding: 0;
  border: 1px solid;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  ${(props) => {
    switch (props.variant) {
      case "danger":
        return `
          background: var(--alert-error-bg);
          border-color: var(--alert-error-border);
          color: var(--accent-error);
          &:hover {
            background: var(--alert-error-border);
            border-color: var(--alert-error-border);
          }
        `;
      case "secondary":
        return `
          background: var(--bg-secondary);
          border-color: var(--border-color);
          color: var(--text-secondary);
          &:hover {
            background: var(--border-color);
            border-color: var(--border-input);
          }
        `;
      case "edit":
        return `
          background: var(--alert-info-bg);
          border-color: var(--alert-info-border);
          color: var(--alert-info-text);
          &:hover {
            background: var(--alert-info-bg);
            border-color: var(--alert-info-border);
          }
        `;
      default:
        return `
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: #ffffff;
          &:hover {
            background: var(--accent-primary-hover);
            border-color: var(--accent-primary-hover);
          }
        `;
    }
  }}
`;

const ToggleSwitch = styled.label<{ checked: boolean; disabled?: boolean }>`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => (props.checked ? "var(--accent-primary)" : "var(--border-input)")};
    border-radius: 24px;
    transition: all 0.3s;

    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: ${(props) => (props.checked ? "23px" : "3px")};
      bottom: 3px;
      background: var(--bg-card);
      border-radius: 50%;
      transition: all 0.3s;
    }
  }
`;

const RequisitesSection = styled.td`
  padding: 0;
  background: var(--bg-primary);
  border-top: 1px solid var(--border-color);
  width: 100%;
`;

const RequisitesContainer = styled.div`
  padding: 1rem 2rem;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
  }
`;

const RequisitesTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const RequisiteItem = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto auto;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: var(--bg-glass);
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  border: 1px solid var(--border-color);

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const RequisiteInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const RequisiteMethod = styled.div`
  font-weight: 600;
  color: var(--text-primary);
`;

const RequisiteData = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const RequisiteLimits = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.25rem;
`;

const RequisiteActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  min-width: fit-content;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

// Статистика реквизита
const RequisiteStatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  min-width: 280px;
`;

const StatsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const StatsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
`;

const StatsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const StatsLabel = styled.span`
  font-size: 0.7rem;
  color: var(--text-muted);
`;

const StatsValue = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const InfoTooltip = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
  color: var(--text-muted);

  &:hover {
    color: var(--text-secondary);
  }
  
  &:hover .tooltip-content {
    visibility: visible;
    opacity: 1;
  }
`;

const TooltipContent = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bg-tooltip);
  color: var(--text-tooltip);
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  width: max-content;
  max-width: 280px;
  z-index: 1000;
  box-shadow: var(--shadow-md);
  transition: opacity 0.2s, visibility 0.2s;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: var(--text-primary);
  }
`;

interface HoldersTableProps {
  traderId: string;
  searchParams: any;
}

const HoldersTable: React.FC<HoldersTableProps> = ({
  traderId,
  searchParams,
}) => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const [expandedHolders, setExpandedHolders] = useState<Set<string>>(
    new Set()
  );

  // Состояние для модального окна устройств
  const [deviceModal, setDeviceModal] = useState<{
    isOpen: boolean;
    holderId: string;
    holderName: string;
    deviceId?: string;
    deviceInfo?: any;
  }>({
    isOpen: false,
    holderId: "",
    holderName: "",
  });

  // Очищаем пустые параметры перед отправкой запроса
  const cleanSearchParams = Object.entries(searchParams).reduce(
    (acc, [key, value]) => {
      // Пропускаем пустые строки и undefined значения
      if (value !== "" && value !== undefined && value !== null) {
        // Конвертируем числовые поля
        if (key === "page" || key === "limit" || key === "bankId") {
          const numValue = Number(value);
          if (!isNaN(numValue) && numValue > 0) {
            acc[key] = numValue;
          }
        } else if (key === "status") {
          // Конвертируем boolean для статуса
          if (value === "true") acc[key] = true;
          else if (value === "false") acc[key] = false;
        } else {
          // Для строковых полей оставляем как есть
          acc[key] = value;
        }
      }
      return acc;
    },
    {} as any
  );

  // Устанавливаем дефолтные значения для обязательных параметров
  const finalParams = {
    ...cleanSearchParams,
    page: cleanSearchParams.page || 1,
    limit: cleanSearchParams.limit || 20,
  };

  const {
    data: holdersData,
    isLoading,
    error,
    refetch: refetchHolders,
  } = useGetTraderHoldersQuery(
    {
      traderId,
      ...finalParams,
    },
    {
      skip: !traderId, // Пропускаем запрос если нет traderId
    }
  );

  const [toggleHolder] = useToggleHolderMutation();
  const [deleteHolder] = useDeleteHolderMutation();
  const [toggleRequisite] = useToggleNewRequisiteMutation();
  const [deleteRequisite] = useDeleteNewRequisiteMutation();

  const holders = holdersData?.data || [];

  const toggleExpanded = (holderId: string) => {
    const newExpanded = new Set(expandedHolders);
    if (newExpanded.has(holderId)) {
      newExpanded.delete(holderId);
    } else {
      newExpanded.add(holderId);
    }
    setExpandedHolders(newExpanded);
  };

  const handleToggleHolder = async (holderId: string, isActive: boolean, holder: any) => {
    try {
      await toggleHolder({ holderId, isActive }).unwrap();
      toast.success(`Держатель ${isActive ? "активирован" : "деактивирован"}`);
    } catch (error: any) {
      toast.error(error?.data?.message || "Ошибка при изменении статуса");
    }
  };

  const handleDeleteHolder = async (holderId: string, holderName: string) => {
    if (
      window.confirm(
        `Вы уверены, что хотите удалить держателя "${holderName}" со всеми его реквизитами?`
      )
    ) {
      try {
        await deleteHolder({ holderId, cascade: true }).unwrap();
        toast.success("Держатель удален");
      } catch (error: any) {
        toast.error(error?.data?.message || "Ошибка при удалении держателя");
      }
    }
  };

  const handleToggleRequisite = async (
    requisiteId: string,
    isActive: boolean
  ) => {
    try {
      await toggleRequisite({ requisiteId, isActive }).unwrap();
      toast.success(`Реквизит ${isActive ? "активирован" : "деактивирован"}`);
    } catch (error: any) {
      toast.error(error?.data?.message || "Ошибка при изменении статуса");
    }
  };

  const handleDeleteRequisite = async (requisiteId: string) => {
    if (window.confirm("Вы уверены, что хотите удалить этот реквизит?")) {
      try {
        await deleteRequisite(requisiteId).unwrap();
        toast.success("Реквизит удален");
      } catch (error: any) {
        toast.error(error?.data?.message || "Ошибка при удалении реквизита");
      }
    }
  };

  const getMethodDisplayName = (method: string) => {
    const methodMap: Record<string, string> = {
      CARD_NUMBER: "Банковская карта",
      SBP: "СБП",
      ACCOUNT_TRANSFER: "Банковский перевод",
      CASH: "Наличные",
      TRANSGOV: "TransGov",
    };
    return methodMap[method] || method;
  };

  // Форматирование числа с разделителями тысяч
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("ru-RU").format(Math.round(num));
  };

  // Форматирование статистики: "completed (completed + pending) / limit"
  const formatStats = (completed: number, pending: number, limit?: number) => {
    const total = completed + pending;
    const limitStr = limit ? ` / ${formatNumber(limit)}` : "";
    if (pending > 0) {
      return `${completed}(${total})${limitStr}`;
    }
    return `${completed}${limitStr}`;
  };

  // Форматирование суммы статистики
  const formatAmountStats = (completedAmount: number, pendingAmount: number, limit?: number, currency?: string) => {
    const total = completedAmount + pendingAmount;
    const currencyStr = currency ? ` ${currency}` : "";
    const limitStr = limit ? ` / ${formatNumber(limit)}${currencyStr}` : currencyStr;
    if (pendingAmount > 0) {
      return `${formatNumber(completedAmount)} (${formatNumber(total)})${limitStr}`;
    }
    return `${formatNumber(completedAmount)}${limitStr}`;
  };

  // Обработчики для устройств
  const handleBindDevice = (holderId: string) => {
    const holder = holders.find((h) => h.id === holderId);
    if (!holder) return;

    setDeviceModal({
      isOpen: true,
      holderId,
      holderName: `${holder.firstName} ${holder.lastName}${holder.middleName ? ` ${holder.middleName}` : ''}`,
    });
  };

  const handleRebindDevice = async (deviceId: string, holderId: string) => {
    const holder = holders.find((h) => h.id === holderId);
    if (!holder) return;

    try {
      // Получаем информацию об устройстве
      const devicesResponse = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3001/api"}/devices?holderId=${holderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (devicesResponse.ok) {
        const devices = await devicesResponse.json();
        const device = devices.data?.find((d: any) => d.id === deviceId);

        setDeviceModal({
          isOpen: true,
          holderId,
          holderName: `${holder.firstName} ${holder.lastName}${holder.middleName ? ` ${holder.middleName}` : ''}`,
          deviceId,
          deviceInfo: device
            ? {
                platform: device.platform,
                model: device.model,
                lastSeenAt: device.lastSeenAt,
                isActive: device.isActive,
                health: device.health,
              }
            : undefined,
        });
      } else {
        // Если не удалось получить информацию, открываем с базовыми данными
        setDeviceModal({
          isOpen: true,
          holderId,
          holderName: `${holder.firstName} ${holder.lastName}${holder.middleName ? ` ${holder.middleName}` : ''}`,
          deviceId,
          deviceInfo: {
            platform: "неизвестно",
            model: "неизвестно",
            lastSeenAt: undefined,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching device info:", error);
      toast.error("Ошибка получения информации об устройстве");
    }
  };

  const closeDeviceModal = () => {
    setDeviceModal({
      isOpen: false,
      holderId: "",
      holderName: "",
    });
  };

  if (!traderId || isLoading) {
    return (
      <TableContainer>
        <LoadingState>
          {!traderId ? "Загрузка профиля..." : "Загрузка держателей..."}
        </LoadingState>
      </TableContainer>
    );
  }

  if (error) {
    console.error("❌ HoldersTable Error:", error);
    return (
      <TableContainer>
        <EmptyState>
          <div
            style={{
              fontWeight: 600,
              marginBottom: "0.5rem",
              color: "var(--accent-error)",
            }}
          >
            Ошибка загрузки держателей
          </div>
          <div style={{ fontSize: "0.875rem" }}>
            {(error as any)?.data?.message ||
              "Произошла ошибка при загрузке данных"}
          </div>
        </EmptyState>
      </TableContainer>
    );
  }

  if (holders.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
            Держатели реквизитов не найдены
          </div>
          <div>
            Создайте первого держателя реквизитов, чтобы начать добавлять
            платежные данные
          </div>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: "40px" }}></TableHeaderCell>
            <TableHeaderCell>Держатель</TableHeaderCell>
            {/* VP-205: колонка «Страна» убрана — страна больше не на держателе. */}
            <TableHeaderCell>Реквизиты</TableHeaderCell>
            <TableHeaderCell>Устройство</TableHeaderCell>
            <TableHeaderCell>Статус</TableHeaderCell>
            <TableHeaderCell>Действия</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {holders.map((holder) => (
            <React.Fragment key={holder.id}>
              <TableRow
                clickable
                expanded={expandedHolders.has(holder.id)}
                onClick={() => toggleExpanded(holder.id)}
              >
                <TableCell>
                  <ExpandButton expanded={expandedHolders.has(holder.id)}>
                    ▶
                  </ExpandButton>
                </TableCell>
                <TableCell>
                  <HolderInfo>
                    <HolderName>
                      {holder.firstName} {holder.lastName}
                      {holder.middleName && ` ${holder.middleName}`}
                    </HolderName>
                    {/* VP-205: phoneNumber/country у держателя удалены —
                        в деталях оставляем только тег. */}
                    <HolderDetails>
                      {holder.tag || ""}
                    </HolderDetails>
                  </HolderInfo>
                </TableCell>
                <TableCell>
                  <div style={{ fontWeight: 600 }}>
                    {holder.requisites?.length || 0} реквизитов
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                    {holder.requisites?.filter((r) => r.isActive).length || 0}{" "}
                    активных
                  </div>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <DeviceStatusButton
                      holderId={holder.id}
                      onBindDevice={handleBindDevice}
                      onRebindDevice={handleRebindDevice}
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <StatusBadge active={holder.isActive}>
                    {holder.isActive ? "Активен" : "Неактивен"}
                  </StatusBadge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <ToggleSwitch 
                      checked={holder.isActive}
                      title={holder.isActive ? "Деактивировать держателя" : "Активировать держателя"}
                    >
                      <input
                        type="checkbox"
                        checked={holder.isActive}
                        onChange={(e) =>
                          handleToggleHolder(holder.id, e.target.checked, holder)
                        }
                      />
                      <span className="slider"></span>
                    </ToggleSwitch>
                    <ActionButton
                      variant="edit"
                      onClick={() => dispatch(setEditingHolder(holder))}
                      title="Редактировать держателя"
                    >
                      <EditIcon />
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() =>
                        handleDeleteHolder(
                          holder.id,
                          `${holder.firstName} ${holder.lastName}${holder.middleName ? ` ${holder.middleName}` : ''}`
                        )
                      }
                      title="Удалить держателя"
                    >
                      <DeleteIcon />
                    </ActionButton>
                  </div>
                </TableCell>
              </TableRow>

              {expandedHolders.has(holder.id) && (
                <TableRow>
                  <RequisitesSection colSpan={7}>
                    <RequisitesContainer>
                      <RequisitesTitle>
                        Реквизиты держателя ({holder.requisites?.length || 0})
                      </RequisitesTitle>
                      {holder.requisites?.length ? (
                        holder.requisites.map((requisite) => (
                          <RequisiteItem key={requisite.id}>
                            <RequisiteInfo>
                              <RequisiteMethod>
                                {getMethodDisplayName(requisite.method)}
                              </RequisiteMethod>
                              <RequisiteData>
                                {requisite.maskedData}
                                <div style={{ fontSize: "0.75rem", color: "var(--accent-primary)", fontWeight: 500, marginTop: "0.25rem" }}>
                                    {requisite.bank?.name} • {requisite.currencyCode}
                                </div>
                                {requisite.tag && ` • ${requisite.tag}`}
                              </RequisiteData>
                              {requisite.limits.length > 0 && (
                                <RequisiteLimits>
                                  Лимиты:{" "}
                                  {requisite.limits
                                    .map(
                                      (limit) =>
                                        `${limit.minAmount.toLocaleString()}-${limit.maxAmount.toLocaleString()} ${
                                          limit.currency
                                        }`
                                    )
                                    .join(", ")}
                                </RequisiteLimits>
                              )}
                              {requisite.cooldownSec > 0 && (
                                <RequisiteLimits>
                                  Задержка между выдачами:{" "}
                                  {Math.floor(requisite.cooldownSec / 60)} мин
                                  {requisite.availableAt &&
                                    new Date(requisite.availableAt) >
                                      new Date() && (
                                      <span
                                        style={{
                                          color: "var(--accent-error)",
                                          marginLeft: "0.5rem",
                                        }}
                                      >
                                        (доступен{" "}
                                        {new Date(
                                          requisite.availableAt
                                        ).toLocaleTimeString()}
                                        )
                                      </span>
                                    )}
                                </RequisiteLimits>
                              )}
                            </RequisiteInfo>
                            
                            {/* Блок статистики — по центру */}
                            {requisite.statistics ? (
                              <RequisiteStatsContainer>
                                <StatsColumn>
                                  <StatsHeader>
                                    Сегодня
                                    <InfoTooltip>
                                      <InfoIcon />
                                      <TooltipContent className="tooltip-content">
                                        <strong>Формат: выполнено (всего) / лимит</strong><br />
                                        • Выполнено — успешные платежи<br />
                                        • Всего — выполнено + в ожидании
                                      </TooltipContent>
                                    </InfoTooltip>
                                  </StatsHeader>
                                  <StatsRow>
                                    <StatsLabel>Платежей</StatsLabel>
                                    <StatsValue>
                                      {formatStats(
                                        requisite.statistics.today.completedCount,
                                        requisite.statistics.today.pendingCount,
                                        requisite.dailyPaymentLimit
                                      )}
                                    </StatsValue>
                                  </StatsRow>
                                  <StatsRow>
                                    <StatsLabel>Оборот</StatsLabel>
                                    <StatsValue>
                                      {formatAmountStats(
                                        requisite.statistics.today.completedAmount,
                                        requisite.statistics.today.pendingAmount,
                                        requisite.dailyLimit,
                                        requisite.currencyCode
                                      )}
                                    </StatsValue>
                                  </StatsRow>
                                </StatsColumn>
                                <StatsColumn>
                                  <StatsHeader>
                                    За все время
                                    <InfoTooltip>
                                      <InfoIcon />
                                      <TooltipContent className="tooltip-content">
                                        <strong>Формат: выполнено (всего) / лимит</strong><br />
                                        • Выполнено — успешные платежи<br />
                                        • Всего — выполнено + в ожидании
                                      </TooltipContent>
                                    </InfoTooltip>
                                  </StatsHeader>
                                  <StatsRow>
                                    <StatsLabel>Платежей</StatsLabel>
                                    <StatsValue>
                                      {formatStats(
                                        requisite.statistics.allTime.completedCount,
                                        requisite.statistics.allTime.pendingCount,
                                        requisite.paymentLimit
                                      )}
                                    </StatsValue>
                                  </StatsRow>
                                  <StatsRow>
                                    <StatsLabel>Оборот</StatsLabel>
                                    <StatsValue>
                                      {formatAmountStats(
                                        requisite.statistics.allTime.completedAmount,
                                        requisite.statistics.allTime.pendingAmount,
                                        requisite.maxTotalVolumeLimit,
                                        requisite.currencyCode
                                      )}
                                    </StatsValue>
                                  </StatsRow>
                                </StatsColumn>
                              </RequisiteStatsContainer>
                            ) : <div />}
                            <RequisiteActions>
                              <StatusBadge active={requisite.isActive}>
                                {requisite.isActive ? "Активен" : "Неактивен"}
                              </StatusBadge>
                              <ToggleSwitch checked={requisite.isActive}>
                                <input
                                  type="checkbox"
                                  checked={requisite.isActive}
                                  onChange={(e) =>
                                    handleToggleRequisite(
                                      requisite.id,
                                      e.target.checked
                                    )
                                  }
                                />
                                <span className="slider"></span>
                              </ToggleSwitch>
                              <ActionButton
                                variant="edit"
                                onClick={() =>
                                  dispatch(setEditingRequisite(requisite))
                                }
                                title="Редактировать реквизит"
                              >
                                <EditIcon />
                              </ActionButton>
                              <ActionButton
                                variant="danger"
                                onClick={() =>
                                  handleDeleteRequisite(requisite.id)
                                }
                                title="Удалить реквизит"
                              >
                                <DeleteIcon />
                              </ActionButton>
                            </RequisiteActions>
                          </RequisiteItem>
                        ))
                      ) : (
                        <div
                          style={{
                            textAlign: "center",
                            color: "var(--text-secondary)",
                            fontStyle: "italic",
                          }}
                        >
                          У этого держателя пока нет реквизитов
                        </div>
                      )}
                    </RequisitesContainer>
                  </RequisitesSection>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      {/* ====== Mobile Card View ====== */}
      <MobileHoldersList>
        {holders.map((holder) => (
          <MobileHolderCard key={holder.id} expanded={expandedHolders.has(holder.id)}>
            <MobileHolderHeader onClick={() => toggleExpanded(holder.id)}>
              <ExpandButton expanded={expandedHolders.has(holder.id)}>
                ▶
              </ExpandButton>
              <MobileHolderInfo>
                <MobileHolderName>
                  {holder.firstName} {holder.lastName}
                  {holder.middleName && ` ${holder.middleName}`}
                </MobileHolderName>
                {/* VP-205: phoneNumber/country у держателя удалены. */}
                <MobileHolderMeta>
                  {holder.tag && <span>{holder.tag}</span>}
                </MobileHolderMeta>
              </MobileHolderInfo>
              <StatusBadge active={holder.isActive}>
                {holder.isActive ? "Акт" : "Выкл"}
              </StatusBadge>
            </MobileHolderHeader>

            <MobileHolderStats>
              <MobileStatItem>
                <MobileStatLabel>Реквизиты</MobileStatLabel>
                <MobileStatValue>{holder.requisites?.length || 0}</MobileStatValue>
              </MobileStatItem>
              <MobileStatItem>
                <MobileStatLabel>Активных</MobileStatLabel>
                <MobileStatValue>{holder.requisites?.filter((r) => r.isActive).length || 0}</MobileStatValue>
              </MobileStatItem>
            </MobileHolderStats>

            <MobileHolderActions>
              <ToggleSwitch
                checked={holder.isActive}
                title={holder.isActive ? "Деактивировать" : "Активировать"}
              >
                <input
                  type="checkbox"
                  checked={holder.isActive}
                  onChange={(e) => handleToggleHolder(holder.id, e.target.checked, holder)}
                />
                <span className="slider"></span>
              </ToggleSwitch>
              <span style={{ flex: 1 }} />
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <DeviceStatusButton
                  holderId={holder.id}
                  onBindDevice={handleBindDevice}
                  onRebindDevice={handleRebindDevice}
                />
              </div>
              <ActionButton
                variant="edit"
                onClick={() => dispatch(setEditingHolder(holder))}
                title="Редактировать"
              >
                <EditIcon />
              </ActionButton>
              <ActionButton
                variant="danger"
                onClick={() => handleDeleteHolder(
                  holder.id,
                  `${holder.firstName} ${holder.lastName}${holder.middleName ? ` ${holder.middleName}` : ''}`
                )}
                title="Удалить"
              >
                <DeleteIcon />
              </ActionButton>
            </MobileHolderActions>

            {expandedHolders.has(holder.id) && (
              <MobileRequisitesList>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                  Реквизиты ({holder.requisites?.length || 0})
                </div>
                {holder.requisites?.length ? (
                  holder.requisites.map((requisite) => (
                    <MobileRequisiteCard key={requisite.id}>
                      <MobileReqHeader>
                        <MobileReqMethod>{getMethodDisplayName(requisite.method)}</MobileReqMethod>
                        <StatusBadge active={requisite.isActive}>
                          {requisite.isActive ? "Акт" : "Выкл"}
                        </StatusBadge>
                      </MobileReqHeader>
                      <MobileReqDetails>
                        <div>{requisite.maskedData}</div>
                        <div style={{ color: 'var(--accent-primary)', fontWeight: 500 }}>
                          {requisite.bank?.name} • {requisite.currencyCode}
                        </div>
                        {requisite.limits.length > 0 && (
                          <div style={{ marginTop: '0.25rem' }}>
                            Лимиты: {requisite.limits.map(
                              (limit) => `${limit.minAmount.toLocaleString()}-${limit.maxAmount.toLocaleString()} ${limit.currency}`
                            ).join(", ")}
                          </div>
                        )}
                        {requisite.cooldownSec > 0 && (
                          <div>Задержка: {Math.floor(requisite.cooldownSec / 60)} мин</div>
                        )}
                      </MobileReqDetails>
                      <MobileReqActions>
                        <ToggleSwitch checked={requisite.isActive}>
                          <input
                            type="checkbox"
                            checked={requisite.isActive}
                            onChange={(e) => handleToggleRequisite(requisite.id, e.target.checked)}
                          />
                          <span className="slider"></span>
                        </ToggleSwitch>
                        <span style={{ flex: 1 }} />
                        <ActionButton
                          variant="edit"
                          onClick={() => dispatch(setEditingRequisite(requisite))}
                          title="Редактировать"
                        >
                          <EditIcon />
                        </ActionButton>
                        <ActionButton
                          variant="danger"
                          onClick={() => handleDeleteRequisite(requisite.id)}
                          title="Удалить"
                        >
                          <DeleteIcon />
                        </ActionButton>
                      </MobileReqActions>
                    </MobileRequisiteCard>
                  ))
                ) : (
                  <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8125rem', padding: '0.5rem', fontStyle: 'italic' }}>
                    Нет реквизитов
                  </div>
                )}
              </MobileRequisitesList>
            )}
          </MobileHolderCard>
        ))}
      </MobileHoldersList>

      {/* Модальное окно для привязки устройств */}
      <QRBindingModal
        isOpen={deviceModal.isOpen}
        onClose={closeDeviceModal}
        holderId={deviceModal.holderId}
        holderName={deviceModal.holderName}
        deviceId={deviceModal.deviceId}
        deviceInfo={deviceModal.deviceInfo}
        onDeviceStatusChanged={refetchHolders}
      />
    </TableContainer>
  );
};

export default HoldersTable;
